import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from 'apollo-react/components/Sidebar';

const normalizePath = (path) => (path || '').replace(/\/$/, '');

/**
 * Get expanded state for parent menus that contain the given path.
 */
const isPathUnderBase = (currentPath, basePath) => {
  if (!basePath) return false;
  const normCurrent = normalizePath(currentPath);
  const normBase = normalizePath(basePath);
  return normCurrent === normBase || normCurrent.startsWith(`${normBase}/`);
};

const getExpandedStateForPath = (menuItems, currentPath) => {
  const state = {};
  const normCurrent = normalizePath(currentPath);
  const isCategoryDetailPath = normCurrent.match(/^\/admin\/categories\/\d+$/);

  menuItems.forEach((item, index) => {
    if (item.menuItems && item.menuItems.length > 0) {
      const key = item.menuKey != null ? item.menuKey : `menu-${index}`;
      const hasActiveChild = item.menuItems.some(
        (sub) => sub.pathname && normCurrent === normalizePath(sub.pathname)
      );
      const hasChildMatchingPath = item.menuItems.some((sub) => {
        if (!sub.pathname) return false;
        const normSub = normalizePath(sub.pathname);
        if (normSub === '/admin/categories' && isCategoryDetailPath) {
          return false;
        }
        return isPathUnderBase(currentPath, sub.pathname);
      });
      if (hasActiveChild || hasChildMatchingPath) {
        state[key] = true;
      }
    }
  });
  return state;
};

/**
 * Unified Sidebar component.
 *
 * Uses the useMenuItems hook to determine the correct menu items
 * based on the logged-in user's role (admin vs customer).
 *
 * Props:
 *   - isSidebarCollapsed: boolean
 *   - setIsSidebarCollapsed: (collapsed: boolean) => void
 */
const UnifiedSidebar = ({ menuItems, ...props }) => {
  const location = useLocation();
  const currentPath = normalizePath(location.pathname);

  const [selectedPath, setSelectedPath] = useState(currentPath);
  const [expandedStates, setExpandedStates] = useState({});

  const menuItemsRef = useRef(menuItems);
  menuItemsRef.current = menuItems;
  useEffect(() => {
    setSelectedPath(currentPath);
  }, [currentPath]);

  useEffect(() => {
    setExpandedStates((prev) => ({
      ...prev,
      ...getExpandedStateForPath(menuItemsRef.current || [], currentPath),
    }));
  }, [currentPath]);
  useEffect(() => {
    const isCategoriesPath = currentPath.startsWith('/admin/categories');
    if (
      !isCategoriesPath &&
      sessionStorage.getItem('sidebarCategoriesContext')
    ) {
      sessionStorage.removeItem('sidebarCategoriesContext');
    }
  }, [currentPath]);

  const handleExpandedChange = useCallback((menuKey, expanded) => {
    setExpandedStates((prev) => ({
      ...prev,
      [menuKey]: expanded,
    }));
  }, []);

  const handleMenuItemClick = useCallback((item) => {
    if (item.pathname) {
      setSelectedPath(item.pathname);
    }
    (item.onClick ?? item.handleOnClick)?.();
  }, []);

  const fromSidebarCategories =
    location.state?.fromSidebarCategories === true ||
    sessionStorage.getItem('sidebarCategoriesContext') === 'true';

  const checkIsActive = useCallback(
    (item) => {
      const isCategoriesMenu =
        item.text === 'Categories' ||
        (item.menuItems &&
          item.menuItems.some((sub) =>
            sub.pathname?.startsWith('/admin/categories')
          ));
      if (isCategoriesMenu && !fromSidebarCategories) {
        return false;
      }
      if (item.pathname) {
        return normalizePath(item.pathname) === selectedPath;
      }
      if (item.menuItems && item.menuItems.length > 0) {
        return item.menuItems.some(
          (sub) => sub.pathname && normalizePath(sub.pathname) === selectedPath
        );
      }
      return false;
    },
    [selectedPath, fromSidebarCategories]
  );

  const menuItemsUpdated = useMemo(() => {
    return (menuItems || []).map((item, index) => {
      const key = item.menuKey != null ? item.menuKey : `menu-${index}`;
      const updatedItem = {
        text: item.text,
        icon: item.icon,
        pathname: item.pathname,
        onClick: item.handleOnClick,
        href: item.href,
      };

      if (item.menuItems && item.menuItems.length > 0) {
        updatedItem.expanded = expandedStates[key] ?? false;
        updatedItem.onExpandedChange = (expanded) =>
          handleExpandedChange(key, expanded);
        updatedItem.menuItems = item.menuItems.map((sub) => ({
          text: sub.text,
          pathname: sub.pathname,
          onClick: sub.handleOnClick,
        }));
      }

      return updatedItem;
    });
  }, [menuItems, expandedStates, handleExpandedChange]);

  return (
    <Sidebar
      sx={{
        '& .MuiPaper-root': {
          width: '320px',
          background: 'linear-gradient(#250056, #0076AE)',
        },
      }}
      logoComponentProps={{
        orgName: 'IQVIA',
        appName: 'Workflow',
      }}
      menuItems={menuItemsUpdated}
      collapsed={props.isSidebarCollapsed}
      onToggleCollapsed={() => {
        props.setIsSidebarCollapsed?.(!props.isSidebarCollapsed);
      }}
      onClick={handleMenuItemClick}
      checkIsActive={checkIsActive}
    />
  );
};

export default UnifiedSidebar;
